import { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ZipAutofillInput — 5-digit ZIP field. When the user finishes typing 5 digits,
 * hits /api/geo/zip/{zip} (free Zippopotam.us cached in Mongo) and calls
 * `onResolve({ city, state, state_abbr, country })` so parent can populate the
 * City/State inputs. No API key required.
 *
 * Props:
 *   value        current ZIP string
 *   onChange     called on every keystroke with new value
 *   onResolve    called with the geo doc when a 5-digit lookup succeeds
 *   testid, placeholder, className, disabled, autoComplete
 */
export default function ZipAutofillInput({
  value,
  onChange,
  onResolve,
  testid,
  placeholder = 'ZIP',
  className,
  disabled,
  autoComplete = 'postal-code',
  ...rest
}) {
  const lastLookup = useRef(null);

  useEffect(() => {
    const zip = (value || '').trim();
    if (zip.length !== 5 || !/^\d{5}$/.test(zip)) return;
    if (lastLookup.current === zip) return;
    lastLookup.current = zip;
    const controller = new AbortController();
    fetch(`${API}/geo/zip/${zip}`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) onResolve && onResolve(d); })
      .catch(() => {});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange && onChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoComplete={autoComplete}
      inputMode="numeric"
      maxLength={5}
      data-testid={testid}
      {...rest}
    />
  );
}
